import { useRef, useState, type JSX } from 'react';

import type {
  ProfCheckpointEncounter,
  ProfCheckpointResult,
} from '../api/prof-checkpoint';
import { ApiError } from '../api/client';
import { useTokens } from '../state/theme';

export type { ProfCheckpointEncounter, ProfCheckpointResult };

type Phase =
  | { kind: 'ready' }
  | { kind: 'submitting'; answer: boolean }
  | { kind: 'error'; answer: boolean }
  | { kind: 'expired' }
  | { kind: 'complete'; result: ProfCheckpointResult };

export interface ProfCheckpointCardProps {
  encounter: ProfCheckpointEncounter;
  onSubmit: (answer: boolean) => Promise<ProfCheckpointResult>;
  onRefresh: () => void;
  /** OA1 — server-graded chat answers arrive already complete: the student
   *  answered by typing/dictating and the server graded exact-once, so the
   *  card renders directly in its outcome state (no buttons). */
  initialResult?: ProfCheckpointResult;
}

export function ProfCheckpointCard({
  encounter,
  onSubmit,
  onRefresh,
  initialResult,
}: ProfCheckpointCardProps): JSX.Element {
  const tokens = useTokens();
  const [phase, setPhase] = useState<Phase>(
    initialResult ? { kind: 'complete', result: initialResult } : { kind: 'ready' },
  );
  const inFlight = useRef(false);

  const submit = async (answer: boolean) => {
    if (inFlight.current || phase.kind === 'complete') return;
    inFlight.current = true;
    setPhase({ kind: 'submitting', answer });
    try {
      const result = await onSubmit(answer);
      setPhase({ kind: 'complete', result });
    } catch (error) {
      setPhase(error instanceof ApiError && error.status === 410
        ? { kind: 'expired' }
        : { kind: 'error', answer });
    } finally {
      inFlight.current = false;
    }
  };

  const disabled = phase.kind !== 'ready';
  const selectedAnswer = phase.kind === 'submitting' || phase.kind === 'error'
    ? phase.answer
    : phase.kind === 'complete'
      ? phase.result.studentAnswer
      : null;

  return (
    <section
      aria-label="Verifica ufficiale facoltativa"
      style={{
        marginTop: 12,
        padding: 14,
        border: `1px solid ${tokens.line}`,
        borderRadius: tokens.radius.md,
        background: tokens.surface,
        display: 'grid',
        gap: 10,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <strong style={{ fontFamily: tokens.fonts.body, color: tokens.text }}>
          Verifica ufficiale
        </strong>
        <span style={{ fontSize: 11, color: tokens.textMuted }}>Facoltativa</span>
      </div>
      <p style={{ margin: 0, color: tokens.text, lineHeight: 1.45 }}>
        {encounter.questionText}
      </p>

      {phase.kind !== 'complete' && phase.kind !== 'expired' && (
        <div style={{ display: 'flex', gap: 8 }}>
          {([
            { label: 'Vero', answer: true },
            { label: 'Falso', answer: false },
          ] as const).map(({ label, answer }) => (
            <button
              key={label}
              type="button"
              disabled={disabled}
              aria-pressed={selectedAnswer === answer}
              onClick={() => { void submit(answer); }}
              style={{
                minHeight: 44,
                minWidth: 84,
                borderRadius: tokens.radius.pill,
                border: `1px solid ${selectedAnswer === answer ? tokens.accent : tokens.line}`,
                background: selectedAnswer === answer ? tokens.accentSoft : tokens.surface,
                color: tokens.text,
                fontWeight: 700,
                cursor: disabled ? 'default' : 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {phase.kind === 'submitting' && (
        <div aria-live="polite" style={{ color: tokens.textMuted }}>
          Sto salvando la verifica…
        </div>
      )}
      {phase.kind === 'error' && (
        <div role="alert" style={{ color: tokens.wrong }}>
          Non sono riuscito a salvare la verifica.
          {' '}
          <button
            type="button"
            onClick={() => { void submit(phase.answer); }}
            style={{ color: tokens.wrong, fontWeight: 700 }}
          >
            Riprova
          </button>
        </div>
      )}
      {phase.kind === 'expired' && (
        <div role="alert" style={{ color: tokens.wrong }}>
          Questa verifica è scaduta.
          {' '}
          <button
            type="button"
            onClick={onRefresh}
            style={{ color: tokens.wrong, fontWeight: 700 }}
          >
            Prepara una nuova verifica
          </button>
        </div>
      )}
      {phase.kind === 'complete' && (
        <div
          role="status"
          style={{ color: phase.result.correct ? tokens.right : tokens.wrong, fontWeight: 700 }}
        >
          {phase.result.correct
            ? 'Risposta corretta. Progressi aggiornati.'
            : `Risposta non corretta. La risposta corretta è ${phase.result.correctAnswer ? 'Vero' : 'Falso'}. Progressi aggiornati.`}
        </div>
      )}
    </section>
  );
}
